import React, { useEffect, useRef, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Button, FormControl, CircularProgress } from "@mui/material";
import { InputOutlined } from "@mui/icons-material";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css"; // Import the CSS for the phone input
import { TextField } from "@mui/material";
import { isMobile } from "react-device-detect";
import { useNavigate, useSearchParams } from "react-router-dom";
import { debounce } from "lodash";
import CustomSnackbar from "../../components/CustomSnackbar";
import { api } from "../../utils/api";
import { fetchVehicleDetails } from "../../utils/fetchVehicleDetails";
import reformatPhoneNumber from "../../utils/reformatPhoneNumber";
import { useAuth0 } from "@auth0/auth0-react";
import {
  getFieldsFromNevdisDetails,
  VEHICLE_TYPE_OPTIONS
} from "../../utils/mapNevdisBodyType";
import {
  getCurrentP1Region,
  getVehicleStateOptions
} from "../../utils/p1Region";
const CryptoJS = require("crypto-js");

// AU states (for rego display); NZ regions when P1 is NZ. getCurrentP1Region also controls
// mobile: AU-only when "AU" or unset, NZ-only when "NZ".
const isNzRegion = getCurrentP1Region() === "NZ";

function isVehicleSetupBlock(data) {
  return (
    data?.errorCode === "RECENT_CANCELLED_VEHICLE_SUBSCRIPTION" ||
    data?.errorCode === "DUPLICATE_VEHICLE_PRODUCT"
  );
}
const VEHICLE_STATE_OPTIONS = getVehicleStateOptions();

const validationSchema = Yup.object().shape({
  personalDetails: Yup.object().shape({
    firstName: Yup.string().required("First Name is required"),
    lastName: Yup.string().required("Last Name is required"),
    mobile: Yup.string()
      .test("mobile", isNzRegion ? "Invalid New Zealand mobile number" : "Invalid Australian mobile number", (value) => {
        if (!value) return false;
        const n = (value || "").trim().replace(/\s/g, "");
        if (isNzRegion) return /^(\+?64|0)2[12789]\d{6,8}$/.test(n);
        return /^(?:\+61|0)[1-9]\d{8}$/i.test(n);
      })
      .required("Mobile is required"),
    email: Yup.string().email("Invalid email address").required("Email is required")
  }),
    vehicleDetails: Yup.object().shape({
    vehicleState: Yup.string().required("Vehicle state or region is required"),
    vehicleRegistration: Yup.string().required("Vehicle Registration is required"),
    vin: Yup.string()
      .nullable()
      .transform((v) => v || "")
      .matches(/^$|^[a-zA-Z0-9]{17}$/, "VIN must be exactly 17 alphanumeric characters if provided"),
    make: Yup.string().trim().required("Make is required"),
    model: Yup.string().trim().required("Model is required")
  }),
  verification: Yup.object().shape({
    enterCode: Yup.string().required("Enter Code is required")
    // verificationMethodInput: Yup.string().email("Invalid email address").required("Email is required"),
    // verificationMethodSMS: Yup.string().matches(/^(?:\+61|0)[1-9]\d{8}$/i, "Invalid Australian mobile number").required("Mobile is required")
  })
});

const steps = [
  {
    title: "Welcome!",
    subHeading: isMobile ? "Thank you for downloading the app." : "Personal Details are"
  },
  {
    title: "Vehicle Info",
    subHeading: ""
  },
  {
    title: "Verification",
    subHeading: ""
  },
  {
    title: "Success",
    subHeading: ""
  }
];

const ActivateMembershipWithInviteCode = () => {
  let navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [verified, setVerified] = useState(false);
  const [queryParameters] = useSearchParams();
  const [hasRetrieved, setHasRetrieved] = useState(false);
  const [customerId, setCustomerId] = useState(null);
  const [vehicleId, setVehicleId] = useState(null);
  const [customerVehicle, setCustomerVehicle] = useState([]);
  const [userExists, setUserExists] = useState(false);
  const [sentOTP, updateSentOTP] = useState(false);
  const [askVehicleDetails, setAskVehicleDetails] = useState(false);
  const { isAuthenticated, user, logout, loginWithRedirect } = useAuth0();
  const [snackbar, setSnackbar] = useState({ message: "", variant: "" });
  const [resendCountdown, setResendCountdown] = useState(0);
  const [showUpdateContact, setShowUpdateContact] = useState(false);
  const [editableEmail, setEditableEmail] = useState("");
  const [editablePhone, setEditablePhone] = useState("");
  const [fetchingVehicleDetails, setFetchingVehicleDetails] = useState(false);
  const [vehicleLookupComplete, setVehicleLookupComplete] = useState(false);
  const [savingVehicleDetails, setSavingVehicleDetails] = useState(false);
  const [regoSupportBlock, setRegoSupportBlock] = useState(null);
  // Coffee Pass invites have no vehicle step; the backend keeps the placeholder vehicle.
  const [isCoffeePass, setIsCoffeePass] = useState(false);
  const latestRegoRef = useRef("");

  const [inviteCode, setInviteCode] = useState(
    queryParameters.has("code") ? queryParameters.get("code").toUpperCase() : ""
  );
  const [initialValues, setInitialValues] = useState({
    personalDetails: {
      firstName: "",
      lastName: "",
      mobile: "",
      email: ""
    },
    vehicleDetails: {
      vehicleState: "", // Default state
      vehicleRegistration: "",
      vin: "",
      make: "",
      model: "",
      color: "",
      vehicleType: ""
    },
    verification: {
      enterCode: "",
      email: "",
      phone: ""
    }
  });

  const updatePersonalDetails = async (key, value) => {
    const dataToSend = {};

    if (key && value) {
      dataToSend[key] = value;
    }
    await api(`v1/customer/${customerId}/${key}`, "PATCH", dataToSend);
  };

  const updateVehicleDetails = async (key, value) => {
    if (!vehicleId) return;
    const dataToSend = {};
    if (key && value !== undefined && value !== null) {
      dataToSend[key] = value;
    }
    if (Object.keys(dataToSend).length === 0) return;
    const res = await api(`v1/vehicle/${vehicleId}/updatedVehicleDetail`, "PATCH", dataToSend);
    if (
      key === "rego" &&
      isVehicleSetupBlock(res?.data) &&
      String(value || "").trim().toUpperCase() === latestRegoRef.current
    ) {
      setRegoSupportBlock(res.data);
    }
  };

  // Save all vehicle details in one call before proceeding from step 1 - ensures rego is persisted
  const saveAllVehicleDetails = async (vehicleDetails) => {
    if (!vehicleId) {
      console.error("[Activation] Cannot save vehicle details: vehicleId is null");
      return false;
    }
    const { vehicleRegistration, vin, make, model } = vehicleDetails || initialValues.vehicleDetails;
    const dataToSend = {};
    if (vehicleRegistration?.trim()) dataToSend.rego = vehicleRegistration.trim();
    if (vin !== undefined && vin !== null) dataToSend.vin = vin || "";
    if (make?.trim()) dataToSend.make = make.trim();
    if (model?.trim()) dataToSend.model = model.trim();
    if (Object.keys(dataToSend).length === 0) return { ok: true };
    try {
      const res = await api(`v1/vehicle/${vehicleId}/updatedVehicleDetail`, "PATCH", dataToSend);
      if (isVehicleSetupBlock(res?.data)) {
        return { ok: false, blocked: true, payload: res.data };
      }
      return { ok: res?.status === 200 && res?.data?.success };
    } catch (err) {
      console.error("[Activation] Failed to save vehicle details:", err);
      return { ok: false };
    }
  };

  const getCode = async () => {
    if (queryParameters.has("code")) {
      const getCodeInfo = await api(`v1/inviteCode/code/${inviteCode}`, "GET", null);

      console.log("Get customer details with code: ", getCodeInfo);

      if (getCodeInfo?.status === 200 && getCodeInfo?.data?.success) {
        const data = getCodeInfo.data.data;
        const customer = data.customer;
        const values = { ...initialValues };

        setCustomerVehicle(data.vehicle);
        setCustomerId(customer._id);
        setVehicleId(data.vehicle._id);
        setIsCoffeePass(data.vertical === "cafe");

        values.personalDetails = {
          firstName: customer.firstName,
          lastName: customer.lastName,
          // mobile: reformatPhoneNumber(customer.phone),
          mobile: customer.phone,
          email: customer.email,
          password: ""
        };

        setInitialValues(values);
        setUserExists(true);
        checkAuth0Exists(customer.email);
      } else {
        setInitialValues({
          personalDetails: {
            firstName: "",
            lastName: "",
            mobile: "",
            email: "",
            password: ""
          },
          vehicleDetails: {
            vehicleRegistration: "",
            vehicleState: "",
            vin: "",
            make: "",
            model: "",
            color: "",
            vehicleType: ""
          },
          verification: {
            enterCode: "",
            email: "",
            phone: ""
          }
        });
        setUserExists(true);
      }

      setHasRetrieved(true);
    } else {
      // No ?code= yet — still show the invite entry form (e.g. success page handoff).
      setHasRetrieved(true);
    }
  };

  const getCustomerVehicles = async () => {
    const vehicleDetails = await api(`v1/vehicle/${customerVehicle._id}`, "GET", null);
    console.log("vehicleDetails: ", vehicleDetails);
    if (vehicleDetails.status == 200) {
      const data = vehicleDetails.data.data;
      const success = vehicleDetails.data.success;
      if (success) {
        var values = { ...initialValues };
        let vehicle = data.vehicle;
        values.vehicleDetails = {
          vehicleRegistration: vehicle.rego,
          // vehicleState: vehicle.state,
          vin: vehicle.vin,
          make: vehicle.make,
          model: vehicle.model,
          color: vehicle.color || "",
          vehicleType: vehicle.vehicleType || ""
        };

        const vin = vehicle.vin;
        const isCustomVIN = /^(\d{14})-([a-fA-F0-9]{32})$/.test(vin);
        const isDefaultRego = vehicle.rego === "DEFAULTREGO";
        const isDefaultVIN = vehicle.vin === "DEFAULTVINNUMBERS";
        const isDefaultMake = vehicle.make === "DEFAULTMAKE";
        const isDefaultModel = vehicle.model === "DEFAULTMODEL";

        // If any vehicle details are default values, enable editing
        if (isCustomVIN || isDefaultRego || isDefaultVIN || isDefaultMake || isDefaultModel) {
          setAskVehicleDetails(true);
          values.vehicleDetails.vin = "";
          // TODO: WHEN ASKING FOR VEHICLE DETAILS NEED TO FETCH FROM SAME API WE'RE USING ON CLIPPED. SEE THIS PAGE: https://my.clippedassist.com.au/register-and-book-repair-job
        }

        // If any fields have default values, clear them so user can enter real values
        if (isDefaultRego) {
          values.vehicleDetails.vehicleRegistration = "";
        }
        if (isDefaultVIN) {
          values.vehicleDetails.vin = "";
        }
        if (isDefaultMake) {
          values.vehicleDetails.make = "";
        }
        if (isDefaultModel) {
          values.vehicleDetails.model = "";
        }

        if (queryParameters.has("selfsignup") && queryParameters.get("selfsignup")) {
          setAskVehicleDetails(true);
          values.vehicleDetails.vin = "";
          values.vehicleDetails.vehicleRegistration = "";
          values.vehicleDetails.make = "";
          values.vehicleDetails.model = "";
          values.vehicleDetails.color = "";
          values.vehicleDetails.vehicleType = "";
        }
        setInitialValues(values);
      }
    }
  };

  const checkAuth0Exists = async (email) => {
    const response = await api(`v1/authUser/check`, "POST", { email: email });
    if (response?.status === 200) {
      setUserExists(Boolean(response?.data?.exists));
    } else {
      setUserExists(true);
    }
  };

  const updateEmailAndPhone = async () => {
    if (!editableEmail || !editablePhone) {
      setSnackbar({ variant: "error", message: "Please enter both email and phone number." });
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editableEmail)) {
      setSnackbar({ variant: "error", message: "Please enter a valid email address." });
      return;
    }

    try {
      // Update email
      await updatePersonalDetails("email", editableEmail);

      // Update phone
      await updatePersonalDetails("phone", editablePhone);

      // Refresh customer data
      const getCodeInfo = await api(`v1/inviteCode/code/${inviteCode}`, "GET", null);
      if (getCodeInfo?.status === 200 && getCodeInfo?.data?.success) {
        const data = getCodeInfo.data.data;
        const customer = data.customer;
        const values = { ...initialValues };

        values.personalDetails = {
          ...values.personalDetails,
          email: customer.email,
          mobile: customer.phone
        };

        setInitialValues(values);
        setShowUpdateContact(false);
        setSnackbar({
          variant: "success",
          message: "Email and phone updated successfully. You can now request a new OTP."
        });
        updateSentOTP(false); // Reset OTP state so they can request a new one
        setResendCountdown(0);
      }
    } catch (error) {
      setSnackbar({ variant: "error", message: "Failed to update email and phone. Please try again." });
    }
  };

  const sendOTP = async (mobileFromForm) => {
    if (customerId !== null) {
      // Send OTP to the mobile number shown on the form (account's current mobile), not just DB value
      const body = { customerId };
      if (mobileFromForm && String(mobileFromForm).trim()) {
        body.mobile = String(mobileFromForm).trim();
      }
      const OTP = await api(`v1/authentication/otp`, "POST", body);
      console.log("OTP Request: ", OTP.data);
      if (OTP.status == 200) {
        const success = OTP.data.success;
        if (success) {
          updateSentOTP(true);
          setResendCountdown(60); // Start 60-second countdown
          // Show message that OTP was sent to both channels
          if (OTP.data.message) {
            setSnackbar({ variant: "success", message: OTP.data.message });
          }
        }
      } else {
        setSnackbar({ variant: "error", message: OTP.data.message || "Failed to send OTP. Please try again." });
      }
    } else {
      alert("Cannot send OTP request, please refresh and try again.");
    }
  };

  const createAuthUser = async () => {
    if (!userExists) {
      let password = initialValues?.personalDetails?.password;
      if (!password) {
        password = "MyPriorityOne123!";
      }
      const create = await api(`v1/authUser`, "POST", {
        email: initialValues.personalDetails.email,
        password: password
      });
      console.log("create: ", create);
      if (create.data.success) {
        await navigateToFinish();
      } else {
        alert("Failed to confirm, please press confirm code to try again.");
      }
    } else {
      await navigateToFinish();
    }
  };

  const validateOneTimeCode = async (codeInput) => {
    const isOtpValid = await api(`v1/authentication/otp/validate`, "POST", {
      OTP: codeInput,
      customerId: customerId,
      vehicleId: vehicleId
    });
    if (isOtpValid.status == 200) {
      await createAuthUser();
      // alert("Activation Complete. Redirecting....");
    } else {
      alert(isOtpValid.data.message);
    }
  };

  // Straight to Auth0 for the verified email: login when the account exists (activation has just
  // created it from the password above), signup only if it somehow does not.
  const continueToAccount = async () => {
    const email = String(initialValues?.personalDetails?.email || "").trim();
    if (isAuthenticated && email && user?.email?.toLowerCase() === email.toLowerCase()) {
      navigate("/my-subscriptions");
      return;
    }

    let accountExists = true;
    try {
      const check = await api(`v1/authUser/check`, "POST", { email });
      if (check?.status === 200 && check?.data?.exists === false) accountExists = false;
    } catch (error) {
      console.error("Auth0 account check failed; defaulting to login:", error);
    }

    await loginWithRedirect({
      authorizationParams: {
        screen_hint: accountExists ? "login" : "signup",
        login_hint: email,
        // Always ask: Auth0 may still hold another account's session even when this app has no local login.
        prompt: "login"
      }
    });
  };

  const navigateToFinish = async () => {
    try {
      const sendWelcomeEmail = await api("v1/customer/welcome", "POST", {
        idUser: customerId,
        vehicleId: customerVehicle._id
      });

      if (sendWelcomeEmail.status === 200) {
        await continueToAccount();
      } else {
        const message = sendWelcomeEmail.data?.message || "Unable to complete activation. Please contact support.";
        alert(message);
      }
    } catch (error) {
      console.error("Activation error:", error);
      alert("An error occurred during activation. Please try again or contact support.");
    }
  };

  const nextStep = async () => {
    if (step === 1 && savingVehicleDetails) return; // Prevent double execution
    if (step === 0 && isCoffeePass) {
      setStep(2);
      return;
    }
    if (step === 0) {
      getCustomerVehicles();
    }

    // Require rego, state/region, make and model to proceed; VIN is optional
    if (step === 1) {
      const { vehicleRegistration, vehicleState, make, model } = initialValues.vehicleDetails;
      if (!vehicleRegistration || !vehicleState) {
        alert(isNzRegion ? "Please enter your vehicle registration and select a region." : "Please enter your vehicle registration and select a state.");
        return;
      }
      if (!make?.trim() || !model?.trim()) {
        alert("Please enter the vehicle make and model.");
        return;
      }

      // Explicitly save vehicle details (rego, vin, make, model) before proceeding to avoid race condition
      setSavingVehicleDetails(true);
      const saved = await saveAllVehicleDetails(initialValues.vehicleDetails);
      setSavingVehicleDetails(false);
      if (saved?.blocked) {
        setRegoSupportBlock(saved.payload);
        return;
      }
      if (!saved?.ok) {
        setSnackbar({ variant: "error", message: "Failed to save vehicle details. Please try again." });
        return;
      }
      setRegoSupportBlock(null);
    }

    setStep(step + 1);
  };

  const prevStep = () => {
    setStep(isCoffeePass && step === 2 ? 0 : step - 1);
  };

  const handleVehicleStateChange = debounce(async (setFieldValue, rego, state) => {
    if (!state) return;

    setInitialValues((prevValues) => ({
      ...prevValues,
      vehicleDetails: {
        ...prevValues.vehicleDetails,
        vehicleState: state
      }
    }));

    const normalizedRego = String(rego || "").trim();
    if (!normalizedRego) return;

    // NZ currently uses manual entry in activation flow.
    if (isNzRegion) {
      setVehicleLookupComplete(true);
      return;
    }

    setFetchingVehicleDetails(true);
    setVehicleLookupComplete(false);
    try {
      const vehicleDetails = await fetchVehicleDetails(normalizedRego, state);
      if (vehicleDetails) {
        console.log("Fetched From NDVIS:", vehicleDetails);
        const fields = getFieldsFromNevdisDetails(vehicleDetails);

        setInitialValues((prevValues) => ({
          ...prevValues,
          vehicleDetails: {
            ...prevValues.vehicleDetails,
            vehicleState: state,
            vin: fields.vin,
            make: fields.make,
            model: fields.model,
            color: fields.color || prevValues.vehicleDetails.color,
            vehicleType: fields.vehicleType || prevValues.vehicleDetails.vehicleType
          }
        }));

        if (fields.vin) updateVehicleDetails("vin", fields.vin);
        if (fields.make) updateVehicleDetails("make", fields.make);
        if (fields.model) updateVehicleDetails("model", fields.model);
        if (fields.color) updateVehicleDetails("color", fields.color);
        if (fields.vehicleType) updateVehicleDetails("vehicleType", fields.vehicleType);

        const typeLabel = fields.vehicleType
          ? VEHICLE_TYPE_OPTIONS.find((option) => option.value === fields.vehicleType)?.label
          : null;
        setSnackbar({
          variant: "success",
          message: typeLabel
            ? `Vehicle details fetched (${fields.bodyType} → ${typeLabel}).`
            : "Vehicle Details fetched for respective state and rego!"
        });
      } else {
        setSnackbar({
          variant: "info",
          message: `No details found for ${normalizedRego.toUpperCase()} in ${state}. Please enter details manually.`
        });

        var values = { ...initialValues };
        const emailHash = CryptoJS.MD5(values.personalDetails.email).toString();

        const currentDate = new Date();
        const formattedDate = currentDate.toISOString().replace(/[-:.]/g, "").replace("T", "");
        const customVIN = formattedDate.substring(0, 14) + "-" + emailHash;

        setInitialValues((prevValues) => ({
          ...prevValues,
          vehicleDetails: {
            ...prevValues.vehicleDetails,
            vin: "",
            make: "",
            model: "",
            color: "",
            vehicleType: ""
          }
        }));

        updateVehicleDetails("vin", customVIN);
        updateVehicleDetails("make", " ");
        updateVehicleDetails("model", " ");
      }
    } catch (error) {
      setSnackbar({
        variant: "error",
        message: "Vehicle lookup unavailable. Please enter details manually."
      });
    } finally {
      setFetchingVehicleDetails(false);
      setVehicleLookupComplete(true);
      setAskVehicleDetails(true);
    }
  }, 500);

  const isLastStep = step === steps.length - 1;

  useEffect(() => {
    // TODO: REMOVE A SESSION ON ACTIVATION TO MAKE SURE YOU'RE STARTING FRESH

    //if (isAuthenticated) {
    // Perform a quiet logout without redirection
    //logout({ returnTo: window.location.href });
    //} else {
    if (hasRetrieved == false) {
      getCode();
    }
    //}
  }, [hasRetrieved, getCode /*, isAuthenticated*/]);

  // Countdown timer for resend button
  useEffect(() => {
    let interval = null;
    if (resendCountdown > 0) {
      interval = setInterval(() => {
        setResendCountdown((prevCountdown) => {
          if (prevCountdown <= 1) {
            return 0;
          }
          return prevCountdown - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [resendCountdown]);

  const handleInviteCodeChange = (event) => {
    let value = event.target.value;
    // Remove any non-alphanumeric characters and convert to uppercase
    value = value.replace(/[^A-Za-z0-9]/g, "");

    setInviteCode(value.toUpperCase());
  };

  const handleFirstNameChange = (event) => {
    // initialValues.personalDetails.firstName = event.target.value;
    var values = { ...initialValues };
    values.personalDetails.firstName = event.target.value;

    setInitialValues(values);
    updatePersonalDetails("firstName", event.target.value);
  };

  const handlePasswordChange = (event) => {
    var values = { ...initialValues };
    values.personalDetails.password = event.target.value;

    setInitialValues(values);
  };

  const handleLastNameChange = (event) => {
    // initialValues.personalDetails.lastName = event.target.value;
    var values = { ...initialValues };
    values.personalDetails.lastName = event.target.value;

    setInitialValues(values);
    updatePersonalDetails("lastName", event.target.value);
  };

  const handleVehicleRegistrationChange = (event, setFieldValue, currentState) => {
    const { value } = event.target;
    latestRegoRef.current = String(value || "").trim().toUpperCase();
    setRegoSupportBlock(null);
    setInitialValues((prevValues) => ({
      ...prevValues,
      vehicleDetails: {
        ...prevValues.vehicleDetails,
        vehicleRegistration: value
      }
    }));
    updateVehicleDetails("rego", value);
    if (currentState && value?.trim()) {
      handleVehicleStateChange(setFieldValue, value, currentState);
    }
  };
  const handleVinChange = (event) => {
    const { value } = event.target;
    setInitialValues((prevValues) => ({
      ...prevValues,
      vehicleDetails: {
        ...prevValues.vehicleDetails,
        vin: value
      }
    }));
    updateVehicleDetails("vin", value);
  };
  const handleMakeChange = (event) => {
    const { value } = event.target;
    setInitialValues((prevValues) => ({
      ...prevValues,
      vehicleDetails: {
        ...prevValues.vehicleDetails,
        make: value
      }
    }));
    updateVehicleDetails("make", value);
  };
  const handleModelChange = (event) => {
    const { value } = event.target;
    setInitialValues((prevValues) => ({
      ...prevValues,
      vehicleDetails: {
        ...prevValues.vehicleDetails,
        model: value
      }
    }));
    updateVehicleDetails("model", value);
  };
  const handleColorChange = (event) => {
    const { value } = event.target;
    setInitialValues((prevValues) => ({
      ...prevValues,
      vehicleDetails: {
        ...prevValues.vehicleDetails,
        color: value
      }
    }));
    updateVehicleDetails("color", value);
  };
  const handleVehicleTypeChange = (event) => {
    const { value } = event.target;
    setInitialValues((prevValues) => ({
      ...prevValues,
      vehicleDetails: {
        ...prevValues.vehicleDetails,
        vehicleType: value
      }
    }));
    if (value) {
      updateVehicleDetails("vehicleType", value);
    }
  };

  return hasRetrieved ? (
    <div className="container activateAccount min-h-screen flex flex-column items-center justify-center gap-8 w-full">
      <h2>{steps[step].title}</h2>
      <p>{steps[step].subHeading}</p>

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting }) => {
          setTimeout(() => {
            setSubmitting(false);
            if (!isLastStep) {
              nextStep();
            }
          }, 400);
        }}
      >
        {({ errors, touched, values, setFieldValue }) => (
          <Form className="flex flex-column gap-[20px] w-full max-w-[600px]">
            {step === 0 && (
              <div className="form-group w-full flex flex-column gap-4">
                <TextField
                  label="Invite Code"
                  name="inviteCode"
                  variant="outlined"
                  fullWidth
                  value={inviteCode}
                  onChange={handleInviteCodeChange}
                  onBlur={getCode}
                  inputProps={{
                    pattern: "^[A-Z0-9]*$" // Regular expression pattern for uppercase letters and numbers
                  }}
                />
                <TextField
                  label="First Name"
                  name="personalDetails.firstName"
                  variant="outlined"
                  fullWidth
                  value={initialValues.personalDetails.firstName}
                  onChange={handleFirstNameChange}
                  InputProps={
                    {
                      // readOnly: true
                    }
                  }
                />
                <TextField
                  label="Last Name"
                  name="personalDetails.lastName"
                  variant="outlined"
                  fullWidth
                  value={initialValues.personalDetails.lastName}
                  onChange={handleLastNameChange}
                  InputProps={
                    {
                      // readOnly: true
                    }
                  }
                />
                <FormControl fullWidth variant="outlined">
                  <Field
                    as={PhoneInput}
                    key={`mobile-${isNzRegion ? "nz" : "au"}`}
                    onlyCountries={isNzRegion ? ["nz"] : ["au"]}
                    country={isNzRegion ? "nz" : "au"}
                    name="personalDetails.mobile"
                    disableCountryCode={true}
                    inputProps={{ id: "mobile", readOnly: true }}
                    variant="outlined"
                  />
                </FormControl>
                <TextField
                  label="Email"
                  name="personalDetails.email"
                  variant="outlined"
                  fullWidth
                  value={initialValues.personalDetails.email}
                  InputProps={{
                    readOnly: true
                  }}
                />
                {!userExists && (
                  <TextField
                    label="Password"
                    name="personalDetails.password"
                    type="password"
                    variant="outlined"
                    onChange={handlePasswordChange}
                    fullWidth
                    value={initialValues.personalDetails.password}
                  />
                )}
                <div className="flex justify-evenly items-center w-full">
                  <Button variant="contained" color="primary" type="submit" onClick={nextStep}>
                    Next
                  </Button>
                </div>
              </div>
            )}
            {step === 1 && (
              <div className="form-group w-full flex flex-column gap-4">
                <p className="text-sm text-gray-600">
                  Enter your vehicle registration and state first. We’ll look up the details, or you can enter them
                  manually.
                </p>
                {regoSupportBlock && (
                  <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-900" role="alert">
                    <p>
                      {regoSupportBlock.message ||
                        "We detected that this vehicle already had a subscription with us that is cancelled. Please contact support to discuss your case."}
                    </p>
                    {regoSupportBlock.data?.supportEmail && (
                      <p className="mt-2">
                        Email:{" "}
                        <a className="underline" href={`mailto:${regoSupportBlock.data.supportEmail}`}>
                          {regoSupportBlock.data.supportEmail}
                        </a>
                      </p>
                    )}
                    {regoSupportBlock.data?.supportPhone && (
                      <p>
                        Phone:{" "}
                        <a className="underline" href={`tel:${String(regoSupportBlock.data.supportPhone).replace(/\s/g, "")}`}>
                          {regoSupportBlock.data.supportPhone}
                        </a>
                      </p>
                    )}
                  </div>
                )}
                <TextField
                  label="Vehicle Registration"
                  name="vehicleDetails.vehicleRegistration"
                  variant="outlined"
                  fullWidth
                  value={initialValues.vehicleDetails.vehicleRegistration}
                  onChange={(e) =>
                    handleVehicleRegistrationChange(e, setFieldValue, initialValues.vehicleDetails.vehicleState)
                  }
                  InputProps={{
                    readOnly: !askVehicleDetails
                  }}
                />
                <div>
                  <label htmlFor="vehicleState">
                    {isNzRegion ? "Region" : "State"} <span className="text-red-500">*</span>
                  </label>
                  <Field
                    as="select"
                    id="vehicleState"
                    name="vehicleDetails.vehicleState"
                    value={initialValues.vehicleDetails.vehicleState}
                    onChange={(e) => {
                      const state = e.target.value;
                      setFieldValue("vehicleDetails.vehicleState", state);
                      handleVehicleStateChange(
                        setFieldValue,
                        initialValues.vehicleDetails.vehicleRegistration,
                        state
                      );
                    }}
                    className="block w-full text-center px-4 py-3 text-base text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  >
                    <option value="">---SELECT {isNzRegion ? "REGION" : "STATE"}---</option>
                    {VEHICLE_STATE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </Field>
                  <ErrorMessage name="vehicleDetails.vehicleState" component="div" />
                </div>

                <TextField
                  label="VIN"
                  name="vehicleDetails.vin"
                  variant="outlined"
                  fullWidth
                  value={initialValues.vehicleDetails.vin}
                  onChange={handleVinChange}
                  InputProps={{
                    readOnly: !askVehicleDetails
                  }}
                />
                <TextField
                  label="Make"
                  name="vehicleDetails.make"
                  variant="outlined"
                  fullWidth
                  value={initialValues.vehicleDetails.make}
                  onChange={handleMakeChange}
                  InputProps={{
                    readOnly: !askVehicleDetails
                  }}
                />
                <TextField
                  label="Model"
                  name="vehicleDetails.model"
                  variant="outlined"
                  fullWidth
                  value={initialValues.vehicleDetails.model}
                  onChange={handleModelChange}
                  InputProps={{
                    readOnly: !askVehicleDetails
                  }}
                />
                <TextField
                  label="Color"
                  name="vehicleDetails.color"
                  variant="outlined"
                  fullWidth
                  value={initialValues.vehicleDetails.color || ""}
                  onChange={handleColorChange}
                  InputProps={{
                    readOnly: !askVehicleDetails
                  }}
                />
                <div>
                  <label htmlFor="vehicleType">Vehicle Type</label>
                  <Field
                    as="select"
                    id="vehicleType"
                    name="vehicleDetails.vehicleType"
                    value={initialValues.vehicleDetails.vehicleType || ""}
                    onChange={handleVehicleTypeChange}
                    disabled={!askVehicleDetails}
                    className="block w-full text-center px-4 py-3 text-base text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  >
                    <option value="">---SELECT VEHICLE TYPE---</option>
                    {VEHICLE_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Field>
                </div>

                <div className="flex justify-evenly items-center w-full">
                  <Button variant="contained" color="primary" type="submit" onClick={prevStep} disabled={savingVehicleDetails}>
                    Previous
                  </Button>
                  {vehicleLookupComplete && (
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      onClick={nextStep}
                      disabled={savingVehicleDetails}
                      startIcon={savingVehicleDetails ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                      {savingVehicleDetails ? "Saving..." : "Next"}
                    </Button>
                  )}
                </div>
              </div>
            )}
            {step === 2 && (
              <div className="form-group w-full flex flex-column gap-4">
                <div className="flex flex-column">
                  {!sentOTP ? (
                    <div className="flex flex-column gap-4">
                      <p className="text-sm">
                        Click the button below to request an OTP code. The code will be sent to both your email (
                        <strong>{initialValues.personalDetails.email}</strong>) and SMS (
                        <strong>{initialValues.personalDetails.mobile}</strong>).{" "}
                        {!showUpdateContact && (
                          <Button
                            variant="text"
                            color="primary"
                            size="small"
                            onClick={() => {
                              setShowUpdateContact(true);
                              setEditableEmail(initialValues.personalDetails.email);
                              setEditablePhone(initialValues.personalDetails.mobile);
                            }}
                            style={{ textTransform: "none", padding: "0 4px", minWidth: "auto" }}
                          >
                            Update
                          </Button>
                        )}
                      </p>
                      {showUpdateContact && (
                        <div className="flex flex-column gap-4 p-4 border rounded">
                          <p className="text-sm font-weight-bold">Update your contact details if they are incorrect:</p>
                          <TextField
                            label="Email"
                            type="email"
                            variant="outlined"
                            fullWidth
                            value={editableEmail}
                            onChange={(e) => setEditableEmail(e.target.value)}
                            required
                          />
                          <FormControl fullWidth variant="outlined">
                            <PhoneInput
                              key={`editablePhone-${isNzRegion ? "nz" : "au"}`}
                              onlyCountries={isNzRegion ? ["nz"] : ["au"]}
                              country={isNzRegion ? "nz" : "au"}
                              value={editablePhone}
                              onChange={(value) => setEditablePhone(value)}
                              disableCountryCode={true}
                              inputProps={{ id: "editablePhone" }}
                            />
                          </FormControl>
                          <div className="flex gap-2">
                            <Button variant="contained" color="primary" onClick={updateEmailAndPhone} fullWidth>
                              Save & Update
                            </Button>
                            <Button
                              variant="outlined"
                              color="secondary"
                              onClick={() => {
                                setShowUpdateContact(false);
                                setEditableEmail("");
                                setEditablePhone("");
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                      <Button
                        variant="contained"
                        color="success"
                        onClick={() => sendOTP(values.personalDetails.mobile)}
                        disabled={resendCountdown > 0 || showUpdateContact}
                        fullWidth
                      >
                        {resendCountdown > 0 ? `Resend OTP in ${resendCountdown}s` : "Request OTP"}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-column gap-4">
                      <Field
                        className="w-full border border-black"
                        placeholder="Enter OTP Code"
                        label="Enter Code"
                        name="verification.enterCode"
                        variant="outlined"
                        fullWidth
                      />
                      <div className="flex gap-2">
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => validateOneTimeCode(values.verification.enterCode)}
                          fullWidth
                        >
                          Confirm Code
                        </Button>
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={() => sendOTP(values.personalDetails.mobile)}
                          disabled={resendCountdown > 0}
                        >
                          {resendCountdown > 0 ? `Resend (${resendCountdown}s)` : "Resend OTP"}
                        </Button>
                      </div>
                      <p className="mt-2 text-sm">
                        OTP code has been sent to both your email and SMS. Contact us on{" "}
                        <a href="mailto:info@priortyonewashclub.com.au">info@priortyonewashclub.com.au</a> if you are
                        having trouble verifying.
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex justify-evenly items-center w-full">
                  <Button variant="contained" color="primary" type="submit" onClick={prevStep}>
                    Previous
                  </Button>
                  {verified && (
                    <Button variant="contained" color="primary" type="submit" onClick={nextStep}>
                      Next
                    </Button>
                  )}
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="form-group w-full flex flex-column gap-4 activate-step3">
                <h2>Success Page</h2>
                <p>Show two CTA HERE</p>
              </div>
            )}
          </Form>
        )}
      </Formik>
      {snackbar && <CustomSnackbar {...snackbar} />}
    </div>
  ) : (
    <div className="flex justify-center items-center h-screen w-full ">
      <p>Looks like you are lost!</p>
    </div>
  );
};

export default ActivateMembershipWithInviteCode;
